import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const SLOT_ORDER = ["in_feed", "rectangle", "leaderboard", "essentials"];
// Standard IAB dimensions — must match the actual rendered container
// in /app/frontend/src/components/AdSlot.jsx. If you resize a slot in
// AdSlot, update the corresponding `dims` here too so advertisers
// upload artwork that fits.
const SLOT_META = {
  in_feed:     { emoji: "📰", label: "In-Feed Sponsored Card",
                 dims: "800×450 px, JPG/PNG/WebP, max 2 MB" },
  rectangle:   { emoji: "📌", label: "Sidebar Rectangle",
                 dims: "300×250 px, JPG/PNG/WebP, max 2 MB" },
  leaderboard: { emoji: "🏆", label: "Hero Leaderboard Banner",
                 dims: "728×90 px (IAB Leaderboard), JPG/PNG/WebP, max 2 MB" },
  essentials:  { emoji: "💎", label: "Island Essentials Sponsor",
                 dims: "300×250 px, JPG/PNG/WebP, max 2 MB" },
};
const ISLANDS = [
  { slug: "all",       label: "All Islands" },
  { slug: "aruba",     label: "Aruba" },
  { slug: "curacao",   label: "Curaçao" },
  { slug: "bonaire",   label: "Bonaire" },
  { slug: "stmaarten", label: "St. Maarten" },
];
const DURATIONS = [
  { w: 1, label: "1 Week" },
  { w: 2, label: "2 Weeks" },
  { w: 4, label: "1 Month" },
];

const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function AdvertiseBookPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [order, setOrder] = useState({
    slot_type: params.get("slot") || "",
    sub_slot: "",
    island_targeting: ["all"],
    duration_weeks: 1,
    start_date: tomorrowStr(),
    headline: "", description: "", cta_label: "", destination_url: "",
    business_name: "", contact_name: "", email: "", whatsapp: "",
    business_island: "aruba",
    terms_accepted: false,
  });
  const [orderId, setOrderId] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState("");
  const [imageError, setImageError] = useState("");
  const [payTab, setPayTab] = useState("sentoo");
  const [setPasswordLink, setSetPasswordLink] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Book an Ad — IslandsDaily";
    api.get("/advertise/config")
      .then((r) => setConfig(r.data))
      .catch((e) => setNotFound(e?.response?.status === 404));
  }, []);

  const priceFor = (slot, weeks) => {
    const p = (config?.pricing || []).find(
      (x) => x.slot_type === slot && x.duration_weeks === weeks
    );
    return p ? p.price_usd : null;
  };
  const totalPrice = useMemo(() => {
    if (!order.slot_type) return null;
    return priceFor(order.slot_type, order.duration_weeks);
  }, [order.slot_type, order.duration_weeks, config]);

  // Session 7 — real-time availability check. When any island in the
  // targeting list is currently locked by a manual admin ad OR another
  // self-serve booking overlapping this date range, block Continue.
  const [availability, setAvailability] = useState({ available: true, reason: "" });
  const [checkingAvail, setCheckingAvail] = useState(false);
  useEffect(() => {
    if (step !== 2 || !order.slot_type || !order.island_targeting.length) return;
    if (!order.start_date || !order.duration_weeks) return;
    // Compute end date locally so the backend can run overlap math
    // against any existing self-serve orders that touch this window.
    const startDate = new Date(order.start_date);
    if (Number.isNaN(startDate.getTime())) return;
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + order.duration_weeks * 7);
    const endStr = endDate.toISOString().slice(0, 10);
    let cancelled = false;
    setCheckingAvail(true);
    (async () => {
      let result = { available: true, reason: "" };
      for (const isl of order.island_targeting) {
        try {
          const r = await api.get("/advertise/availability", {
            params: {
              slot: order.slot_type,
              island: isl,
              start: order.start_date,
              end: endStr,
              sub_slot: order.sub_slot || "",
            },
          });
          if (!r.data?.available) { result = r.data; break; }
        } catch { /* ignore transient errors */ }
      }
      if (!cancelled) { setAvailability(result); setCheckingAvail(false); }
    })();
    return () => { cancelled = true; };
  }, [step, order.slot_type, order.sub_slot, order.island_targeting.join(","),
      order.start_date, order.duration_weeks]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="book-404">
        <div className="text-center">
          <h1 className="font-serif-display text-3xl text-[#1A2E35]">404</h1>
          <Link to="/" className="text-[#0AADA0] mt-4 inline-block">← Home</Link>
        </div>
      </div>
    );
  }
  if (!config) return <div className="p-10 text-center text-[#6B7C82]">Loading…</div>;

  const goNext = () => setStep((s) => Math.min(6, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleImage = (file) => {
    setImageError("");
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setImageError("Only JPG, PNG or WebP accepted.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setImageError("Image exceeds 2 MB.");
      return;
    }
    const rd = new FileReader();
    rd.onload = () => setImageDataUrl(rd.result);
    rd.readAsDataURL(file);
  };

  const submitStep4 = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/advertise/orders", order);
      setOrderId(r.data.order_id);
      await api.post(`/advertise/orders/${r.data.order_id}/image`, {
        image_data_url: imageDataUrl,
        filename: "upload",
      });
      goNext();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create order");
    } finally { setSubmitting(false); }
  };

  const submitPayment = async () => {
    setSubmitting(true);
    try {
      const [sub, pay] = await Promise.all([
        api.post(`/advertise/orders/${orderId}/submit`),
        api.post(`/advertise/orders/${orderId}/pay/sentoo/init`),
      ]);
      if (sub.data.set_password_link) setSetPasswordLink(sub.data.set_password_link);
      // Sentoo not configured yet → show fallback but still submit
      goNext();
    } catch (e) {
      alert(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`book-step-${step}`}>
      <Header />
      <main className="max-w-3xl mx-auto py-8 px-4">
        {/* Progress */}
        <div className="mb-8" data-testid="book-progress">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5,6].map((n) => (
              <div key={n} className="flex-1 h-1.5 rounded-full"
                   style={{ background: n <= step ? "#0AADA0" : "#EEEEEE" }} />
            ))}
          </div>
          <p className="text-[11px] uppercase tracking-wider text-[#6B7C82] mt-2">
            Step {step} of 6
          </p>
        </div>

        {step === 1 && (
          <div data-testid="step-1">
            <h2 className="font-serif-display text-[#1A2E35] text-2xl mb-4">Select Slot</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SLOT_ORDER.map((s) => (
                <button
                  key={s}
                  onClick={() => setOrder({ ...order, slot_type: s })}
                  className={`text-left border rounded-xl p-4 ${order.slot_type === s ? "border-[#0AADA0] ring-2 ring-[#0AADA0]/30" : "border-[#EEEEEE]"}`}
                  data-testid={`select-slot-${s}`}
                >
                  <div className="text-2xl">{SLOT_META[s].emoji}</div>
                  <div className="font-medium mt-1 text-[#1A2E35]">{SLOT_META[s].label}</div>
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                disabled={!order.slot_type}
                onClick={goNext}
                className="bg-[#E63946] disabled:opacity-50 text-white px-4 py-2 rounded-full text-[13px]"
                data-testid="step1-continue"
              >Continue →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-2">
            <h2 className="font-serif-display text-[#1A2E35] text-2xl mb-1">Configure Campaign</h2>
            <p className="text-[13px] text-[#0AADA0] mb-4">
              {SLOT_META[order.slot_type]?.label}
            </p>

            {order.slot_type === "essentials" && (
              <div className="mb-4">
                <label className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">
                  Sponsor which utility?
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {["pharmacy","beach","currency","cruise"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setOrder({ ...order, sub_slot: s })}
                      className={`px-2.5 py-1 rounded-full text-[12px] ${order.sub_slot === s ? "bg-[#0AADA0] text-white" : "bg-[#F5F5F5] text-[#1A1A1A]"}`}
                      data-testid={`sub-slot-${s}`}
                    >{s[0].toUpperCase()+s.slice(1)}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">
                Island targeting (multi-select)
              </label>
              <div className="flex flex-wrap gap-1.5">
                {ISLANDS.map((i) => {
                  const on = order.island_targeting.includes(i.slug);
                  return (
                    <button
                      key={i.slug}
                      onClick={() => setOrder({
                        ...order,
                        island_targeting: on
                          ? order.island_targeting.filter((x) => x !== i.slug)
                          : [...order.island_targeting, i.slug],
                      })}
                      className={`px-2.5 py-1 rounded-full text-[12px] ${on ? "bg-[#0AADA0] text-white" : "bg-[#F5F5F5] text-[#1A1A1A]"}`}
                      data-testid={`island-${i.slug}`}
                    >{i.label}</button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">Duration</label>
              <div className="flex flex-wrap gap-1.5">
                {DURATIONS.map((d) => (
                  <button
                    key={d.w}
                    onClick={() => setOrder({ ...order, duration_weeks: d.w })}
                    className={`px-2.5 py-1 rounded-full text-[12px] ${order.duration_weeks === d.w ? "bg-[#0AADA0] text-white" : "bg-[#F5F5F5] text-[#1A1A1A]"}`}
                    data-testid={`duration-${d.w}`}
                  >{d.label}</button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">Start date</label>
              <input
                type="date"
                min={tomorrowStr()}
                value={order.start_date}
                onChange={(e) => setOrder({ ...order, start_date: e.target.value })}
                className="border border-[#EEEEEE] rounded-lg px-3 py-2 text-sm"
                data-testid="start-date"
              />
            </div>

            <div className="bg-[#F5F5F5] p-4 rounded-lg" data-testid="price-summary">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">Total</div>
              <div className="text-[#0AADA0] font-semibold text-lg">
                ${totalPrice ?? "—"} USD
              </div>
            </div>

            {!availability.available && (
              <div
                className="mt-3 p-3 rounded-lg border border-[#F59E0B] bg-[#FFF7E6] text-[12px] text-[#78350F]"
                data-testid="availability-warning"
              >
                {availability.reason}
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={goBack} className="text-[13px] text-[#6B7C82]">← Back</button>
              <button
                onClick={goNext}
                disabled={totalPrice == null || checkingAvail || !availability.available || (order.slot_type === "essentials" && !order.sub_slot)}
                className="bg-[#E63946] disabled:opacity-50 text-white px-4 py-2 rounded-full text-[13px]"
                data-testid="step2-continue"
              >{checkingAvail ? "Checking…" : "Continue →"}</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-3">
            <h2 className="font-serif-display text-[#1A2E35] text-2xl mb-1">Upload Creative</h2>
            <p className="text-[13px] text-[#6B7C82] mb-4">
              Required: {SLOT_META[order.slot_type]?.dims}
            </p>

            {order.slot_type === "in_feed" && (
              <div className="space-y-3 mb-4">
                <TextField label={`Headline (${order.headline.length}/60)`} maxLength={60}
                           value={order.headline}
                           onChange={(v) => setOrder({ ...order, headline: v })}
                           testid="in-feed-headline" />
                <TextField label={`Description (${order.description.length}/100)`} maxLength={100}
                           value={order.description}
                           onChange={(v) => setOrder({ ...order, description: v })}
                           testid="in-feed-description" />
                <TextField label={`CTA Label (${order.cta_label.length}/20)`} maxLength={20}
                           value={order.cta_label}
                           onChange={(v) => setOrder({ ...order, cta_label: v })}
                           testid="in-feed-cta-label" />
              </div>
            )}

            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">
                Destination URL
              </label>
              <input
                type="url"
                placeholder="https://…"
                value={order.destination_url}
                onChange={(e) => setOrder({ ...order, destination_url: e.target.value })}
                className="w-full border border-[#EEEEEE] rounded-lg px-3 py-2 text-sm"
                data-testid="destination-url"
              />
            </div>

            <div className="border-2 border-dashed border-[#EEEEEE] rounded-xl p-6 text-center">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleImage(e.target.files?.[0])}
                data-testid="image-file-input"
              />
              {imageError && <p className="text-[#E63946] text-[12px] mt-2" data-testid="image-error">{imageError}</p>}
              {imageDataUrl && (
                <img src={imageDataUrl} alt="preview" data-testid="image-preview"
                     className="mx-auto mt-3 max-h-48 rounded-lg" />
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <button onClick={goBack} className="text-[13px] text-[#6B7C82]">← Back</button>
              <button
                onClick={goNext}
                disabled={!imageDataUrl || !order.destination_url}
                className="bg-[#E63946] disabled:opacity-50 text-white px-4 py-2 rounded-full text-[13px]"
                data-testid="step3-continue"
              >Continue →</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-4">
            <h2 className="font-serif-display text-[#1A2E35] text-2xl mb-4">Advertiser Details</h2>
            <div className="space-y-3">
              <TextField label="Business name *" value={order.business_name}
                         onChange={(v) => setOrder({ ...order, business_name: v })}
                         testid="adv-business" />
              <TextField label="Contact name *" value={order.contact_name}
                         onChange={(v) => setOrder({ ...order, contact_name: v })}
                         testid="adv-contact" />
              <TextField label="Email *" value={order.email} type="email"
                         onChange={(v) => setOrder({ ...order, email: v })}
                         testid="adv-email" />
              <TextField label="WhatsApp (optional)" value={order.whatsapp}
                         onChange={(v) => setOrder({ ...order, whatsapp: v })}
                         testid="adv-whatsapp" />
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">
                  Business island
                </label>
                <select
                  value={order.business_island}
                  onChange={(e) => setOrder({ ...order, business_island: e.target.value })}
                  className="border border-[#EEEEEE] rounded-lg px-3 py-2 text-sm"
                  data-testid="adv-island"
                >
                  {ISLANDS.filter((i) => i.slug !== "all").map((i) =>
                    <option key={i.slug} value={i.slug}>{i.label}</option>)}
                </select>
              </div>
              <label className="flex items-start gap-2 text-[13px] text-[#1A2E35]">
                <input
                  type="checkbox"
                  checked={order.terms_accepted}
                  onChange={(e) => setOrder({ ...order, terms_accepted: e.target.checked })}
                  data-testid="adv-terms"
                />
                <span>I agree to <Link to="/advertise/terms" className="text-[#0AADA0]">IslandsDaily Advertising Terms</Link></span>
              </label>
            </div>
            <div className="mt-6 flex justify-between">
              <button onClick={goBack} className="text-[13px] text-[#6B7C82]">← Back</button>
              <button
                onClick={submitStep4}
                disabled={submitting || !order.business_name || !order.contact_name || !order.email || !order.terms_accepted}
                className="bg-[#E63946] disabled:opacity-50 text-white px-4 py-2 rounded-full text-[13px]"
                data-testid="step4-continue"
              >{submitting ? "Saving…" : "Continue →"}</button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="step-5">
            <h2 className="font-serif-display text-[#1A2E35] text-2xl mb-4">Review & Pay</h2>
            <div className="border border-[#EEEEEE] rounded-xl p-4 mb-4 space-y-1 text-[13px]" data-testid="order-summary">
              <div>Slot: <b>{SLOT_META[order.slot_type]?.label}</b></div>
              <div>Islands: {order.island_targeting.join(", ")}</div>
              <div>Duration: {order.duration_weeks === 4 ? "1 Month" : `${order.duration_weeks} Week${order.duration_weeks>1?"s":""}`}</div>
              <div>Start: {order.start_date}</div>
              {imageDataUrl && <img src={imageDataUrl} alt="preview" className="max-h-32 rounded-lg mt-2" />}
              <div>Business: {order.business_name}</div>
              <div className="text-[#0AADA0] font-semibold pt-2">Total: ${totalPrice} USD</div>
            </div>

            <div className="border border-[#EEEEEE] rounded-xl p-4">
              <div className="flex gap-2 mb-3">
                <button onClick={() => setPayTab("sentoo")}
                        className={`text-[13px] px-3 py-1.5 rounded ${payTab === "sentoo" ? "bg-[#F5F5F5]" : ""}`}
                        data-testid="pay-tab-sentoo">Sentoo</button>
                <button onClick={() => setPayTab("card")}
                        className={`text-[13px] px-3 py-1.5 rounded ${payTab === "card" ? "bg-[#F5F5F5]" : ""}`}
                        data-testid="pay-tab-card">Credit Card</button>
              </div>
              {payTab === "sentoo" ? (
                <div>
                  <p className="text-[13px] text-[#1A2E35] mb-3">
                    <b>Sentoo</b> — Pay with Sentoo, the easy way for Aruban businesses.
                  </p>
                  {config.sentoo_configured ? (
                    <button
                      onClick={submitPayment} disabled={submitting}
                      className="bg-[#00B4D8] text-white px-4 py-2 rounded-full text-[13px]"
                      data-testid="pay-sentoo-btn"
                    >{submitting ? "Processing…" : `Pay $${totalPrice} USD`}</button>
                  ) : (
                    <div>
                      <p className="text-[12px] text-[#6B7C82] mb-3" data-testid="sentoo-not-configured">
                        Payment not configured — contact <a href="mailto:hello@islandsdaily.com" className="text-[#0AADA0]">hello@islandsdaily.com</a>
                      </p>
                      <button
                        onClick={submitPayment} disabled={submitting}
                        className="bg-[#E63946] text-white px-4 py-2 rounded-full text-[13px]"
                        data-testid="submit-order-btn"
                      >
                        {submitting ? "Submitting…" : "Submit Order (payment coordinated separately)"}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[13px] text-[#6B7C82]" data-testid="card-coming-soon">
                  Coming soon — contact <a href="mailto:hello@islandsdaily.com" className="text-[#0AADA0]">hello@islandsdaily.com</a> to pay by card.
                </p>
              )}
            </div>
            <div className="mt-6">
              <button onClick={goBack} className="text-[13px] text-[#6B7C82]">← Back</button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="text-center py-10" data-testid="step-6">
            <CheckCircle2 size={64} className="mx-auto text-[#0AADA0]" />
            <h2 className="font-serif-display text-[#1A2E35] text-2xl mt-4">
              Your ad has been submitted!
            </h2>
            <p className="text-[13px] text-[#6B7C82] mt-2">
              We will review your ad within 24 hours and email you at <b>{order.email}</b> when it goes live.
            </p>
            <p className="text-[13px] text-[#1A2E35] mt-4">
              Order reference: <b data-testid="order-ref">#{orderId}</b>
            </p>
            {setPasswordLink && (
              <p className="text-[12px] text-[#6B7C82] mt-4">
                Set your advertiser password: <Link to={setPasswordLink} className="text-[#0AADA0]" data-testid="set-password-link">Set password →</Link>
              </p>
            )}
            <div className="mt-6 flex flex-col gap-1 items-center text-[13px]">
              <Link to="/advertiser/dashboard" className="text-[#0AADA0]">View your advertiser dashboard →</Link>
              <Link to="/" className="text-[#6B7C82]">Back to IslandsDaily →</Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

const TextField = ({ label, value, onChange, maxLength, type = "text", testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-[#6B7C82] block mb-1">{label}</span>
    <input
      type={type}
      value={value}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#EEEEEE] rounded-lg px-3 py-2 text-sm"
      data-testid={testid}
    />
  </label>
);
